import { LoadingSpinner } from "components/LoadingSpinner";
import { parsePrice } from "helpers/price";
import { Button } from "primereact/button";
import { Calendar, CalendarValueType } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { SelectButton } from "primereact/selectbutton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IContractModelingRow,
  IYearComparisonRow,
  useGetContractModelingAvailableCptCodesQuery,
  useGetContractModelingAvailableYearsQuery,
  useGetContractModelingPayersWithDataQuery,
  usePostContractModelingAnalysisMutation,
  usePostContractModelingYearComparisonMutation,
} from "store/queries/contractModeling";
import { useGetAllPayersQuery } from "store/queries/payers";
import styled from "styled-components";

import { exportToCSV } from "../MarketPriceAnalysis/helpers";

const StyledContainer = styled.div`
  border-radius: 8px;
  border: 1px solid var(--gray-new-200);
  display: flex;
  flex-direction: column;
  width: 100%;

  .p-multiselect .p-multiselect-label {
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .p-datatable {
    .bg-primary-100 {
      background-color: #f0f6ff !important;

      td {
        font-weight: 600;
        border-top: 2px solid var(--primary-color);
      }
    }
  }
`;

type ComparisonMode = "payer" | "year";
const MODE_OPTIONS = [
  { label: "Payer comparison", value: "payer" },
  { label: "Year comparison", value: "year" },
];
const MAX_INDIVIDUAL_COLUMNS = 3;

function formatIsoDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDollars(value: number) {
  return parsePrice(value);
}

function getMutationErrorMessage(err: unknown): string {
  if (!err || typeof err !== "object") return "Analysis request failed.";
  const anyErr = err as {
    data?: { message?: string };
    message?: string;
  };
  return anyErr.data?.message || anyErr.message || "Analysis request failed.";
}

// ─── Normalised row type used by both modes for the shared table ──────
interface ITableRow {
  cptCode: string;
  volume: number;
  primaryRevenue: number;
  comparisonRevenues: {
    key: string | number;
    label: string;
    revenue: number;
  }[];
  revenuePotential: number;
  isTotalsRow?: boolean;
  comparisonAvgRevenue?: number;
}

export function ContractModelingAnalysis() {
  const [mode, setMode] = useState<ComparisonMode>("payer");

  // ── shared state ──
  const [selectedCptCodes, setSelectedCptCodes] = useState<string[]>([]);
  const [first, setFirst] = useState(0);
  const [rows] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);

  // ── payer mode state ──
  const [primaryPayerId, setPrimaryPayerId] = useState<number | null>(null);
  const [comparisonPayerIds, setComparisonPayerIds] = useState<number[]>([]);
  const [serviceDateRange, setServiceDateRange] = useState<Date[]>([]);

  // ── year mode state ──
  const [yearPayerId, setYearPayerId] = useState<number | null>(null);
  const [primaryYear, setPrimaryYear] = useState<number | null>(null);
  const [comparisonYears, setComparisonYears] = useState<number[]>([]);

  // ── shared queries ──
  const { data: payersData, isLoading: isLoadingPayersCatalog } =
    useGetAllPayersQuery();
  const {
    data: payersWithModelingData = [],
    isLoading: isLoadingPayersWithData,
  } = useGetContractModelingPayersWithDataQuery();

  // ── payer mode derived ──
  const startDate = useMemo(
    () => formatIsoDate(serviceDateRange[0] ?? null),
    [serviceDateRange]
  );
  const endDate = useMemo(
    () => formatIsoDate(serviceDateRange[1] ?? null),
    [serviceDateRange]
  );

  const { data: cptOptionsPayer = [], isLoading: isLoadingCptsPayer } =
    useGetContractModelingAvailableCptCodesQuery(
      mode === "payer"
        ? { payerId: primaryPayerId ?? undefined, startDate, endDate }
        : undefined,
      { skip: mode !== "payer" }
    );

  const [
    postPayerAnalysis,
    {
      data: payerData,
      isLoading: isLoadingPayerAnalysis,
      error: payerError,
      reset: resetPayerFn,
    },
  ] = usePostContractModelingAnalysisMutation();

  const resetPayerRef = useRef(resetPayerFn);
  resetPayerRef.current = resetPayerFn;
  const resetPayer = useCallback(() => resetPayerRef.current(), []);

  // ── year mode derived ──
  const { data: availableYears = [], isLoading: isLoadingYears } =
    useGetContractModelingAvailableYearsQuery(yearPayerId ?? 0, {
      skip: !yearPayerId,
    });

  const { data: cptOptionsYear = [], isLoading: isLoadingCptsYear } =
    useGetContractModelingAvailableCptCodesQuery(
      mode === "year" && yearPayerId ? { payerId: yearPayerId } : undefined,
      { skip: mode !== "year" || !yearPayerId }
    );

  const [
    postYearComparison,
    {
      data: yearData,
      isLoading: isLoadingYearAnalysis,
      error: yearError,
      reset: resetYearFn,
    },
  ] = usePostContractModelingYearComparisonMutation();

  const resetYearRef = useRef(resetYearFn);
  resetYearRef.current = resetYearFn;
  const resetYear = useCallback(() => resetYearRef.current(), []);

  // ── payer options (shared between both modes) ──
  const payerOptions = useMemo(() => {
    if (!payersWithModelingData.length) return [];
    const catalogById = new Map<number, { name: string; archivedAt?: string }>(
      (payersData?.data ?? []).map(
        (payer: { id: number; name: string; archivedAt?: string }) => [
          payer.id,
          { name: payer.name, archivedAt: payer.archivedAt },
        ]
      )
    );
    return payersWithModelingData
      .map((ref) => {
        const catalog = catalogById.get(ref.id);
        const label = (
          ref.name?.trim() ||
          catalog?.name ||
          `Payer ${ref.id}`
        ).trim();
        if (catalog?.archivedAt) return null;
        if (label === "Clear Health") return null;
        return { label, value: ref.id };
      })
      .filter((o): o is { label: string; value: number } => o !== null)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [payersWithModelingData, payersData]);

  const comparisonPayerOptions = useMemo(
    () =>
      payerOptions.filter(
        (o: { label: string; value: number }) => o.value !== primaryPayerId
      ),
    [payerOptions, primaryPayerId]
  );

  // ── payer mode effects ──
  const distinctComparisonIds = useMemo(() => {
    if (primaryPayerId == null) return [];
    return [...new Set(comparisonPayerIds)].filter(
      (id) => id !== primaryPayerId
    );
  }, [comparisonPayerIds, primaryPayerId]);

  const isPayerAggregated =
    distinctComparisonIds.length > MAX_INDIVIDUAL_COLUMNS;

  useEffect(() => {
    if (mode !== "payer") return;
    setComparisonPayerIds((prev) => {
      const filtered = prev.filter((id) => id !== primaryPayerId);
      return filtered.length === prev.length ? prev : filtered;
    });
    setFirst(0);
    resetPayer();
  }, [primaryPayerId, resetPayer, mode]);

  const payerCompKey = comparisonPayerIds.join(",");
  const cptKey = selectedCptCodes.join(",");

  useEffect(() => {
    setFirst(0);
  }, [payerCompKey, cptKey, startDate, endDate, comparisonYears]);

  useEffect(() => {
    if (mode !== "payer") return;
    if (distinctComparisonIds.length === 0) resetPayer();
  }, [distinctComparisonIds.length, resetPayer, mode]);

  useEffect(() => {
    if (mode !== "payer") return;
    if (!primaryPayerId || distinctComparisonIds.length === 0) return;
    postPayerAnalysis({
      primaryPayerId,
      comparisonPayerIds,
      cptCodes: selectedCptCodes.length > 0 ? selectedCptCodes : undefined,
      startDate,
      endDate,
    });
  }, [primaryPayerId, payerCompKey, cptKey, startDate, endDate, mode]);

  // ── year mode effects ──
  const distinctComparisonYears = useMemo(() => {
    if (primaryYear == null) return [];
    return [...new Set(comparisonYears)].filter((y) => y !== primaryYear);
  }, [comparisonYears, primaryYear]);

  const isYearAggregated =
    distinctComparisonYears.length > MAX_INDIVIDUAL_COLUMNS;

  useEffect(() => {
    if (mode !== "year") return;
    setPrimaryYear(null);
    setComparisonYears([]);
    setSelectedCptCodes([]);
    setFirst(0);
    resetYear();
  }, [yearPayerId, resetYear, mode]);

  useEffect(() => {
    if (mode !== "year") return;
    setComparisonYears((prev) => {
      const filtered = prev.filter((y) => y !== primaryYear);
      return filtered.length === prev.length ? prev : filtered;
    });
    setFirst(0);
    resetYear();
  }, [primaryYear, resetYear, mode]);

  const yearCompKey = comparisonYears.join(",");

  useEffect(() => {
    if (mode !== "year") return;
    if (distinctComparisonYears.length === 0) resetYear();
  }, [distinctComparisonYears.length, resetYear, mode]);

  useEffect(() => {
    if (mode !== "year") return;
    if (!yearPayerId || !primaryYear || distinctComparisonYears.length === 0)
      return;
    postYearComparison({
      payerId: yearPayerId,
      primaryYear,
      comparisonYears,
      cptCodes: selectedCptCodes.length > 0 ? selectedCptCodes : undefined,
    });
  }, [yearPayerId, primaryYear, yearCompKey, cptKey, mode]);

  // Reset shared state when switching modes
  useEffect(() => {
    setFirst(0);
    setSelectedCptCodes([]);
    setIsDownloading(false);
  }, [mode]);

  // ── year dropdown options ──
  const yearOptions = useMemo(
    () => availableYears.map((y) => ({ label: String(y), value: y })),
    [availableYears]
  );

  const comparisonYearOptions = useMemo(
    () => yearOptions.filter((o) => o.value !== primaryYear),
    [yearOptions, primaryYear]
  );

  // ── current active data (normalised for shared table rendering) ──
  const activeIsLoading =
    mode === "payer" ? isLoadingPayerAnalysis : isLoadingYearAnalysis;
  const activeError = mode === "payer" ? payerError : yearError;
  const activeHasComparisons =
    mode === "payer"
      ? distinctComparisonIds.length > 0
      : distinctComparisonYears.length > 0;
  const activePayerSelected =
    mode === "payer" ? primaryPayerId != null : yearPayerId != null;
  const activeYearOrPayerReady = mode === "payer" ? true : primaryYear != null;
  const isAggregated = mode === "payer" ? isPayerAggregated : isYearAggregated;

  const rawRows = mode === "payer" ? payerData?.rows : yearData?.rows;

  const comparisonColumnHeaders = useMemo((): {
    key: string | number;
    label: string;
  }[] => {
    if (!rawRows?.length) return [];
    if (mode === "payer") {
      return (
        (rawRows as IContractModelingRow[])[0]?.comparisonPayers.map((c) => ({
          key: c.payerId,
          label: c.payerName,
        })) ?? []
      );
    }
    return (
      (rawRows as IYearComparisonRow[])[0]?.comparisonYears.map((c) => ({
        key: c.year,
        label: String(c.year),
      })) ?? []
    );
  }, [rawRows, mode]);

  const tableData = useMemo((): ITableRow[] => {
    if (!rawRows?.length) return [];

    const toTableRow = (
      row: IContractModelingRow | IYearComparisonRow
    ): ITableRow => {
      if (mode === "payer") {
        const r = row as IContractModelingRow;
        return {
          cptCode: r.cptCode,
          volume: r.volume,
          primaryRevenue: r.primaryPayer.revenue,
          comparisonRevenues: r.comparisonPayers.map((c) => ({
            key: c.payerId,
            label: c.payerName,
            revenue: c.modeledRevenue,
          })),
          revenuePotential: r.revenuePotential,
        };
      }
      const r = row as IYearComparisonRow;
      return {
        cptCode: r.cptCode,
        volume: r.volume,
        primaryRevenue: r.primaryYear.revenue,
        comparisonRevenues: r.comparisonYears.map((c) => ({
          key: c.year,
          label: String(c.year),
          revenue: c.modeledRevenue,
        })),
        revenuePotential: r.revenuePotential,
      };
    };

    const paginated = rawRows.slice(first, first + rows).map(toTableRow);

    // Build totals row
    let totalPrimary = 0;
    const totalCompMap = new Map<string | number, number>();
    let totalRevPotential = 0;

    if (mode === "payer" && payerData) {
      totalPrimary = payerData.totals.primaryPayerRevenue;
      payerData.totals.comparisonPayerRevenues.forEach((t) => {
        totalCompMap.set(t.payerId, t.totalModeledRevenue);
      });
      totalRevPotential = payerData.totals.revenuePotential.difference;
    } else if (mode === "year" && yearData) {
      totalPrimary = yearData.totals.primaryYearRevenue;
      yearData.totals.comparisonYearRevenues.forEach((t) => {
        totalCompMap.set(t.year, t.totalModeledRevenue);
      });
      totalRevPotential = yearData.totals.revenuePotential.difference;
    }

    const totalCompRevenues = comparisonColumnHeaders.map((h) => ({
      key: h.key,
      label: h.label,
      revenue: totalCompMap.get(h.key) ?? 0,
    }));

    const compCount = totalCompRevenues.length || 1;
    const avgTotal =
      totalCompRevenues.reduce((s, c) => s + c.revenue, 0) / compCount;

    const totalsRow: ITableRow = {
      cptCode: "Totals",
      volume: 0,
      primaryRevenue: totalPrimary,
      comparisonRevenues: totalCompRevenues,
      revenuePotential: totalRevPotential,
      isTotalsRow: true,
      comparisonAvgRevenue: avgTotal,
    };

    return [...paginated, totalsRow];
  }, [
    rawRows,
    first,
    rows,
    mode,
    payerData,
    yearData,
    comparisonColumnHeaders,
  ]);

  const onPageChange = (event: PaginatorPageState) => setFirst(event.first);

  const handleDownloadData = async () => {
    if (!rawRows?.length) return;
    try {
      setIsDownloading(true);
      const allRows = rawRows.map((row) => {
        const r =
          mode === "payer"
            ? {
                cpt: (row as IContractModelingRow).cptCode,
                vol: (row as IContractModelingRow).volume,
                pRev: (row as IContractModelingRow).primaryPayer.revenue,
                comps: (row as IContractModelingRow).comparisonPayers.map(
                  (c) => ({
                    label: c.payerName,
                    revenue: c.modeledRevenue,
                  })
                ),
                rp: (row as IContractModelingRow).revenuePotential,
              }
            : {
                cpt: (row as IYearComparisonRow).cptCode,
                vol: (row as IYearComparisonRow).volume,
                pRev: (row as IYearComparisonRow).primaryYear.revenue,
                comps: (row as IYearComparisonRow).comparisonYears.map((c) => ({
                  label: String(c.year),
                  revenue: c.modeledRevenue,
                })),
                rp: (row as IYearComparisonRow).revenuePotential,
              };

        const base: Record<string, string | number> = {
          CPT: r.cpt,
          Volume: r.vol,
          "Primary revenue": formatDollars(r.pRev),
        };

        if (isAggregated) {
          const count = r.comps.length || 1;
          const avg = r.comps.reduce((s, c) => s + c.revenue, 0) / count;
          base["Comparison avg revenue"] = formatDollars(avg);
        } else {
          r.comps.forEach((c) => {
            base[`${c.label} revenue`] = formatDollars(c.revenue);
          });
        }
        base["Revenue potential"] = formatDollars(r.rp);
        return base;
      });
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const prefix =
        mode === "payer" ? "contract_modeling_payer" : "contract_modeling_year";
      exportToCSV(allRows, `${prefix}_${timestamp}.csv`);
    } finally {
      setIsDownloading(false);
    }
  };

  // ── early returns ──
  if (isLoadingPayersCatalog || isLoadingPayersWithData) {
    return <LoadingSpinner />;
  }

  if (!payersWithModelingData.length) {
    return (
      <StyledContainer className="bg-white">
        <div className="p-4">
          <div className="flex gap-2 align-items-center mb-3">
            <i className="pi pi-table text-2xl text-primary" />
            <p className="text-lg font-bold">Contract modeling</p>
          </div>
          <div className="text-center p-4 text-gray-500">
            No EDI claims with line items were found for this account, so
            contract modeling is not available yet.
          </div>
        </div>
      </StyledContainer>
    );
  }

  const cptOpts = mode === "payer" ? cptOptionsPayer : cptOptionsYear;
  const isCptLoading =
    mode === "payer" ? isLoadingCptsPayer : isLoadingCptsYear;
  const totalRows = rawRows?.length ?? 0;

  return (
    <StyledContainer className="bg-white">
      <div className="p-4">
        <div className="flex justify-content-between align-items-center mb-3">
          <div className="flex gap-2 align-items-center">
            <i className="pi pi-table text-2xl text-primary" />
            <p className="text-lg font-bold">Contract modeling</p>
          </div>
          {rawRows && !activeIsLoading && rawRows.length > 0 && (
            <Button
              label="Download"
              icon="pi pi-download"
              onClick={handleDownloadData}
              loading={isDownloading}
              className="p-button-outlined max-h-3rem max-w-12rem"
            />
          )}
        </div>

        <div className="mb-3">
          <SelectButton
            value={mode}
            options={MODE_OPTIONS}
            onChange={(e) => {
              if (e.value) setMode(e.value as ComparisonMode);
            }}
          />
        </div>

        {/* ── Payer mode filters ── */}
        {mode === "payer" && (
          <div className="flex flex-wrap gap-3 mb-4 align-items-end">
            <div
              className="flex flex-column gap-2"
              style={{ minWidth: "220px" }}
            >
              <label className="text-sm font-semibold">Primary payer</label>
              <Dropdown
                value={primaryPayerId}
                options={payerOptions}
                onChange={(e) => setPrimaryPayerId(e.value ?? null)}
                placeholder="Select primary payer"
                showClear
                filter
              />
            </div>

            <div
              className="flex flex-column gap-2 flex-grow-1"
              style={{ minWidth: "260px" }}
            >
              <label className="text-sm font-semibold">Comparison payers</label>
              <MultiSelect
                value={comparisonPayerIds}
                options={comparisonPayerOptions}
                onChange={(e) => setComparisonPayerIds(e.value ?? [])}
                placeholder={
                  !primaryPayerId
                    ? "Select a primary payer first"
                    : "Select one or more payers to compare"
                }
                filter
                showClear
                display="chip"
                className="w-full"
                disabled={!primaryPayerId}
              />
            </div>

            <div className="flex flex-wrap gap-3 align-items-end">
              <div
                className="flex flex-column gap-2"
                style={{
                  minWidth: "240px",
                  width: "240px",
                  maxWidth: "100%",
                }}
              >
                <label className="text-sm font-semibold">
                  Service date range
                </label>
                <Calendar
                  selectionMode="range"
                  placeholder="Service Date Range"
                  value={serviceDateRange}
                  onChange={(e: { value: CalendarValueType }) => {
                    const dates = e.value as Date[];
                    setServiceDateRange(dates || []);
                  }}
                  showIcon
                  dateFormat="mm/dd/yy"
                  className="w-full"
                />
              </div>

              <div
                className="flex flex-column gap-2"
                style={{ width: "200px", maxWidth: "100%" }}
              >
                <label className="text-sm font-semibold">CPT (optional)</label>
                <MultiSelect
                  value={selectedCptCodes}
                  options={cptOpts.map((c) => ({ label: c, value: c }))}
                  onChange={(e) => setSelectedCptCodes(e.value ?? [])}
                  placeholder={isCptLoading ? "Loading…" : "All if empty"}
                  filter
                  showClear
                  display="comma"
                  maxSelectedLabels={2}
                  className="w-full"
                  disabled={isCptLoading}
                  emptyFilterMessage="No CPT codes match"
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Year mode filters ── */}
        {mode === "year" && (
          <div className="flex flex-wrap gap-3 mb-4 align-items-end">
            <div
              className="flex flex-column gap-2"
              style={{ minWidth: "220px" }}
            >
              <label className="text-sm font-semibold">Payer</label>
              <Dropdown
                value={yearPayerId}
                options={payerOptions}
                onChange={(e) => setYearPayerId(e.value ?? null)}
                placeholder="Select payer"
                showClear
                filter
              />
            </div>

            <div
              className="flex flex-column gap-2"
              style={{ minWidth: "140px" }}
            >
              <label className="text-sm font-semibold">Primary year</label>
              <Dropdown
                value={primaryYear}
                options={yearOptions}
                onChange={(e) => setPrimaryYear(e.value ?? null)}
                placeholder={
                  // eslint-disable-next-line no-nested-ternary
                  isLoadingYears
                    ? "Loading…"
                    : !yearPayerId
                    ? "Select payer first"
                    : "Select year"
                }
                showClear
                disabled={!yearPayerId || isLoadingYears}
              />
            </div>

            <div
              className="flex flex-column gap-2"
              style={{ minWidth: "200px" }}
            >
              <label className="text-sm font-semibold">Comparison years</label>
              <MultiSelect
                value={comparisonYears}
                options={comparisonYearOptions}
                onChange={(e) => setComparisonYears(e.value ?? [])}
                placeholder={
                  !primaryYear ? "Select primary year first" : "Select years"
                }
                display="chip"
                className="w-full"
                disabled={!primaryYear}
              />
            </div>

            <div
              className="flex flex-column gap-2"
              style={{ width: "200px", maxWidth: "100%" }}
            >
              <label className="text-sm font-semibold">CPT (optional)</label>
              <MultiSelect
                value={selectedCptCodes}
                options={cptOpts.map((c) => ({ label: c, value: c }))}
                onChange={(e) => setSelectedCptCodes(e.value ?? [])}
                placeholder={isCptLoading ? "Loading…" : "All if empty"}
                filter
                showClear
                display="comma"
                maxSelectedLabels={2}
                className="w-full"
                disabled={isCptLoading}
                emptyFilterMessage="No CPT codes match"
              />
            </div>
          </div>
        )}

        {/* ── Error ── */}
        {activeError && (
          <div className="p-3 mb-3 border-round border-1 border-red-200 bg-red-50 text-red-900 text-sm">
            {getMutationErrorMessage(activeError)}
          </div>
        )}

        {/* ── Loading ── */}
        {activeIsLoading && (
          <div className="flex justify-content-center p-5">
            <LoadingSpinner />
          </div>
        )}

        {/* ── Table ── */}
        {activePayerSelected &&
          activeYearOrPayerReady &&
          activeHasComparisons &&
          rawRows &&
          !activeIsLoading && (
            <div className="w-full">
              {rawRows.length > 0 ? (
                <>
                  <DataTable
                    value={tableData}
                    stripedRows
                    showGridlines
                    responsiveLayout="scroll"
                    className="mb-4"
                    rowClassName={(rowData) => ({
                      "bg-primary-100 font-bold": rowData.isTotalsRow,
                    })}
                  >
                    <Column
                      field="cptCode"
                      header="CPT Code"
                      style={{ width: "100px" }}
                    />
                    <Column
                      field="volume"
                      header="Volume"
                      body={(row: ITableRow) =>
                        row.isTotalsRow ? "-" : row.volume
                      }
                      style={{ width: "80px" }}
                    />
                    <Column
                      header="Primary Revenue"
                      body={(row: ITableRow) =>
                        formatDollars(row.primaryRevenue)
                      }
                      style={{ width: "130px" }}
                    />
                    {isAggregated ? (
                      <Column
                        header="Comparison avg"
                        body={(row: ITableRow) => {
                          if (row.isTotalsRow) {
                            return formatDollars(row.comparisonAvgRevenue ?? 0);
                          }
                          const count = row.comparisonRevenues.length || 1;
                          const avg =
                            row.comparisonRevenues.reduce(
                              (s, c) => s + c.revenue,
                              0
                            ) / count;
                          return formatDollars(avg);
                        }}
                        style={{ width: "140px" }}
                      />
                    ) : (
                      comparisonColumnHeaders.map((col) => (
                        <Column
                          key={col.key}
                          header={col.label}
                          body={(row: ITableRow) => {
                            const cell = row.comparisonRevenues.find(
                              (c) => c.key === col.key
                            );
                            return cell ? formatDollars(cell.revenue) : "—";
                          }}
                          style={{ width: "140px" }}
                        />
                      ))
                    )}
                    <Column
                      header="Revenue Potential"
                      body={(row: ITableRow) =>
                        formatDollars(row.revenuePotential)
                      }
                      style={{ width: "140px" }}
                    />
                  </DataTable>
                  {totalRows > rows && (
                    <Paginator
                      first={first}
                      rows={rows}
                      totalRecords={totalRows}
                      onPageChange={onPageChange}
                    />
                  )}
                </>
              ) : (
                <div className="text-center p-4 text-gray-500">
                  No CPT rows matched the selected filters.
                </div>
              )}
            </div>
          )}

        {/* ── Empty states ── */}
        {!activePayerSelected && !activeIsLoading && (
          <div className="text-center p-4 text-gray-500">
            {mode === "payer"
              ? "Select a primary payer to run contract modeling."
              : "Select a payer to run year comparison."}
          </div>
        )}

        {activePayerSelected &&
          (!activeYearOrPayerReady || !activeHasComparisons) &&
          !activeIsLoading && (
            <div className="text-center p-4 text-gray-500">
              {/* eslint-disable-next-line no-nested-ternary */}
              {mode === "payer"
                ? "Select at least one comparison payer other than the primary payer."
                : !primaryYear
                ? "Select a primary year."
                : "Select at least one comparison year other than the primary year."}
            </div>
          )}
      </div>
    </StyledContainer>
  );
}
